import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ROOM_STATUSES, fmtNum } from "../lib/format";
import { useAuth, hasRole } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import ExportButtons from "../components/ExportButtons";

export default function Rooms() {
  const { user } = useAuth();
  const { t } = useI18n();
  const canChange = hasRole(user, "admin", "front_office", "hk_supervisor");
  const [rooms, setRooms] = useState([]);
  const [detail, setDetail] = useState(null);
  const [pending, setPending] = useState({});

  const load = () => api.get("/reports/room-status").then(({ data }) => setRooms(data.rooms || []));
  useEffect(() => { load(); }, []);

  const changeStatus = async (roomId, status) => {
    try {
      await api.patch(`/rooms/${roomId}/status`, { status });
      toast.success(`Room status → ${status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const statusBadge = (s) => {
    const map = {
      "Vacant Clean": "bg-emerald-100 text-emerald-800 border-emerald-200",
      "Occupied": "bg-blue-100 text-blue-800 border-blue-200",
      "Checkout Expected": "bg-amber-100 text-amber-800 border-amber-200",
      "Checked Out/Dirty": "bg-red-100 text-red-800 border-red-200",
      "Cleaning in Progress": "bg-slate-100 text-slate-800 border-slate-200",
      "Ready": "bg-emerald-100 text-emerald-800 border-emerald-200",
    };
    return <Badge className={`${map[s] || "bg-slate-100"} border`}>{s}</Badge>;
  };

  return (
    <Layout>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="section-title" data-testid="page-title">{t("nav.rooms")}</h1>
          <div className="section-sub">{t("sub.rooms")}</div>
        </div>
        <ExportButtons endpoint="/reports/export/master-stock" filenameBase="rooms" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
        {rooms.map(r => (
          <Card key={r.room_id} className="p-4" data-testid={`room-card-${r.number}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-serif text-xl text-slate-900">Room {r.number}</div>
                {statusBadge(r.status)}
              </div>
              <Button size="sm" variant="outline" onClick={() => setDetail(r)} data-testid={`room-detail-${r.number}`}>Detail</Button>
            </div>
            {canChange && (
              <div className="mt-3">
                <Select value={pending[r.room_id] || r.status} onValueChange={v => { setPending({ ...pending, [r.room_id]: v }); changeStatus(r.room_id, v); }}>
                  <SelectTrigger className="h-9 text-xs" data-testid={`room-status-select-${r.number}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROOM_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="mt-3 text-xs text-slate-600 space-y-1">
              <div>Total items in room: <span className="font-semibold text-slate-800">{fmtNum(r.items?.reduce((s, x) => s + x.clean, 0))}</span></div>
              <div>Dirty (checkout): <span className="font-semibold text-red-700">{fmtNum(r.items?.reduce((s, x) => s + x.dirty_checkout, 0))}</span></div>
              <div>Shortages: <span className="font-semibold text-amber-700">{r.items?.filter(x => x.shortage > 0).length || 0} items</span></div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Room {detail?.number} · {detail?.status}</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <table className="vr-table">
              <thead><tr><th>Item</th><th className="num">Par</th><th className="num">Clean</th><th className="num">Dirty</th><th className="num">Shortage</th></tr></thead>
              <tbody>
                {(detail?.items || []).map(it => (
                  <tr key={it.item_id}><td>{it.name}{it.size?` · ${it.size}`:""}</td><td className="num">{fmtNum(it.par)}</td><td className="num">{fmtNum(it.clean)}</td><td className="num text-red-700">{fmtNum(it.dirty_checkout)}</td><td className="num text-amber-700">{fmtNum(it.shortage)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
